import { getSessionEstimatedArrivalStatus } from "./product.utils.js";

class ArrivalModal extends HTMLElement {
  constructor() {
    super();

    const defaultArrival = window.dualSaleStandardOptionName;
    const arrival = getSessionEstimatedArrivalStatus() || defaultArrival;

    this.state = {
      arrival,
    };
  }

  connectedCallback() {
    this.initializeElements();
    this.initializeListeners();
    this.updateUI();
  }

  /**
   * Handles the event containing the new state for the arrival.
   * @param {Object} state - The state object containing arrival details.
   */
  handleStateEvent(state) {
    if (!state.arrival) return;
    this.updateState(state);
    this.updateUI();
  }

  /**
   * Updates the component state based on the arrival state.
   * @param {Object} state - The state object containing arrival details.
   */
  updateState(state) {
    this.state.arrival = state.arrival;
  }

  /**
   * Updates the title and description of the modal based on the arrival state.
   */
  updateUI() {
    const { standardText, rushText } = this.messageDescription.dataset;
    const isStandard = this.variantIsStandard();
    const modalTitle = isStandard ? "Standard" : "Rush";
    const modalDescription = isStandard ? standardText : rushText;

    if (isStandard) {
      this.messageLink.classList.remove("hidden");
    } else {
      this.messageLink.classList.add("hidden");
    }

    this.messageTitle.textContent = modalTitle;
    this.messageDescription.textContent = modalDescription;
  }

  variantIsStandard() {
    const arrivalStateLowercased = this.state.arrival?.toLowerCase();
    const isStandard = arrivalStateLowercased.includes("standard");
    return isStandard;
  }

  initializeElements() {
    this.message = this.querySelector(".arrival-modal-message");

    if (this.message !== null) {
      this.triggers = this.querySelectorAll(".arrival-modal-trigger");
      this.overlay = this.querySelector(".arrival-modal-overlay");
      this.close = this.querySelector(".arrival-modal-close");
      this.closeImg = this.querySelector(".arrival-modal-close img");
      this.messageTitle = this.querySelector(".arrival-modal-message-title");
      this.messageDescription = this.querySelector(
        ".arrival-modal-message-description",
      );
      this.messageLink = this.querySelector(".arrival-modal-message-link");
    }
  }

  initializeListeners() {
    this.addEventListener("arrivalStateUpdated", (event) => {
      this.handleStateEvent(event.detail);
    });

    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        if (
          e.target === this.close ||
          e.target === this.closeImg ||
          e.target.tagName === "A"
        ) {
          return;
        }
        e.preventDefault();
        this.showModal();
        this.trapFocus();
      });
    });

    this.close.addEventListener("click", (e) => {
      this.closeModal(e);
    });

    this.overlay.addEventListener("click", (e) => {
      this.closeModal(e);
    });
  }

  showModal() {
    this.message.classList.remove("none");
    this.message.classList.add("active");
    this.overlay.classList.remove("hidden");
  }

  closeModal = (e) => {
    e.preventDefault();
    this.message.classList.add("none");
    this.message.classList.remove("active");
    this.overlay.classList.add("hidden");
  };

  trapFocus() {
    const focusableElements = this.message.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    this.message.addEventListener("keydown", (event) => {
      const isTabPressed = event.key === "Tab" || event.keyCode === 9;

      if (!isTabPressed) {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          event.preventDefault();
        }
      } else if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        event.preventDefault();
      }
    });
  }
}

export default ArrivalModal;
